import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { uploadManualIdPhoto } from '../../util/api';
import {
  MANUAL_UPLOAD_PROMPT,
  MANUAL_UPLOAD_TOGGLE,
  MANUAL_UPLOAD_TITLE,
  MANUAL_UPLOAD_EXAMPLE_CAPTION,
  MANUAL_UPLOAD_CHECKLIST,
  MANUAL_UPLOAD_NOTE,
  manualRejectText,
} from '../../util/identityStatus';

import css from './IdentityManualUpload.module.css';

const MAX_SIDE = 1600;

// Downsize on the device so the upload is ~1 MB. Falls back to the original file.
const fileToDataUrl = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_failed'));
    reader.onload = () => {
      const original = reader.result;
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch (e) {
          resolve(original);
        }
      };
      img.onerror = () => resolve(original);
      img.src = original;
    };
    reader.readAsDataURL(file);
  });

// Example picture: a face with an ID card held right next to it (no real person).
const ExamplePicture = () => (
  <svg
    className={css.exampleSvg}
    viewBox="0 0 240 150"
    role="img"
    aria-label="Example: a person holding their ID card right next to their face, with both fully in the photo"
  >
    <rect
      x="1"
      y="1"
      width="238"
      height="148"
      rx="14"
      fill="#eef4fb"
      stroke="#8aa4c8"
      strokeWidth="2"
    />
    <circle cx="80" cy="62" r="30" fill="#f2c9a5" />
    <path d="M50 58c0-22 13-34 30-34s30 12 30 34c-6-12-16-17-30-17s-24 5-30 17z" fill="#5a3b26" />
    <circle cx="70" cy="62" r="3" fill="#0b2b5b" />
    <circle cx="90" cy="62" r="3" fill="#0b2b5b" />
    <path
      d="M71 75q9 7 18 0"
      stroke="#0b2b5b"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
    />
    <path d="M30 149c4-36 24-50 50-50s46 14 50 50z" fill="#f51f79" />
    <g transform="rotate(-4 175 72)">
      <rect
        x="128"
        y="44"
        width="94"
        height="60"
        rx="7"
        fill="#ffffff"
        stroke="#0b2b5b"
        strokeWidth="2.5"
      />
      <rect
        x="136"
        y="53"
        width="26"
        height="32"
        rx="3"
        fill="#f2c9a5"
        stroke="#0b2b5b"
        strokeWidth="1.5"
      />
      <rect x="168" y="56" width="46" height="5" rx="2.5" fill="#0b2b5b" />
      <rect x="168" y="66" width="38" height="5" rx="2.5" fill="#8aa4c8" />
      <rect x="168" y="76" width="42" height="5" rx="2.5" fill="#8aa4c8" />
      <rect x="136" y="91" width="78" height="5" rx="2.5" fill="#8aa4c8" />
    </g>
    <path
      d="M118 106c6 0 10-8 14-14"
      stroke="#f2c9a5"
      strokeWidth="11"
      fill="none"
      strokeLinecap="round"
    />
    <circle cx="214" cy="128" r="14" fill="#1d7a46" />
    <path
      d="M207 128l5 5 9-10"
      stroke="#ffffff"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const usePreview = f => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!f || typeof URL === 'undefined' || !URL.createObjectURL) {
      setUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(f);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [f]);
  return url;
};

const PhotoPicker = ({ id, label, hint, file, preview, onPick }) => (
  <div className={css.picker} data-manual-id-picker={id}>
    <label htmlFor={id} className={css.fileLabel}>
      {label}
    </label>
    <p id={`${id}-hint`} className={css.hint}>
      {hint}
    </p>
    <input
      id={id}
      className={css.file}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      aria-describedby={`${id}-hint`}
      onChange={e => onPick(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
    />
    <p className={css.chosen} aria-live="polite">
      {file ? `✓ Added: ${file.name}` : 'Not added yet.'}
    </p>
    {preview ? <img className={css.preview} src={preview} alt={`Your ${label}`} /> : null}
  </div>
);

/**
 * "Still stuck?" fallback: one private photo (person holding ID next to face)
 * for a person on our team to check. Collapsed under Try again so Stripe stays
 * the first choice (Oct 17 2026). Shows an example, a 3-point checklist and the
 * automatic photo check's message right under the button.
 * Screen-reader friendly: real <label> on the file input, status in an
 * aria-live region, focus moves to the result.
 *
 * @param {Object} props
 * @param {Function} [props.onUploaded]
 * @param {string} [props.className]
 * @param {string} [props.rejectReason] reason Ops picked when rejecting the last photo
 * @param {boolean} [props.defaultOpen]
 * @returns {JSX.Element}
 */
const IdentityManualUpload = props => {
  const { onUploaded, className, rejectReason, defaultOpen } = props;
  const [open, setOpen] = useState(!!rejectReason || defaultOpen === true);
  const [file, setFile] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [state, setState] = useState('idle'); // idle | sending | error | check
  const [message, setMessage] = useState('');
  const statusRef = useRef(null);

  const preview = usePreview(file);
  const selfiePreview = usePreview(selfie);
  const bothChosen = !!file && !!selfie;

  const focusStatus = () => {
    setTimeout(() => statusRef.current && statusRef.current.focus(), 0);
  };

  const submit = e => {
    e.preventDefault();
    if (!file || !selfie) {
      setState('error');
      setMessage(
        !file && !selfie
          ? 'Please add both photos first.'
          : !file
          ? 'Please add a photo of your ID.'
          : 'Please add a selfie holding your ID.'
      );
      focusStatus();
      return;
    }
    setState('sending');
    setMessage('Checking your photos…');
    Promise.all([fileToDataUrl(file), fileToDataUrl(selfie)])
      .then(([dataUrl, selfieDataUrl]) => uploadManualIdPhoto(dataUrl, selfieDataUrl))
      .then(() => {
        setState('idle');
        setMessage('');
        if (typeof onUploaded === 'function') {
          onUploaded();
        }
      })
      .catch(err => {
        setState(err?.code === 'photo_check' ? 'check' : 'error');
        setMessage(err?.error || "We couldn't send your photos. Please try again.");
        focusStatus();
      });
  };

  if (!open) {
    return (
      <div className={classNames(css.collapsed, className)} data-manual-id-upload="collapsed">
        <button
          type="button"
          className={css.toggle}
          aria-expanded="false"
          aria-controls="manual-id-form"
          onClick={() => setOpen(true)}
          data-manual-id-toggle="true"
        >
          {MANUAL_UPLOAD_TOGGLE}
        </button>
      </div>
    );
  }

  const isProblem = state === 'error' || state === 'check';
  return (
    <form
      id="manual-id-form"
      className={classNames(css.root, className)}
      onSubmit={submit}
      aria-labelledby="manual-id-title"
      data-manual-id-upload="true"
    >
      <h3 id="manual-id-title" className={css.title}>
        {MANUAL_UPLOAD_TITLE}
      </h3>
      {rejectReason ? (
        <p className={css.rejected} role="status" data-manual-id-rejected={rejectReason}>
          {manualRejectText(rejectReason)}
        </p>
      ) : null}
      <p id="manual-id-help" className={css.prompt}>
        {MANUAL_UPLOAD_PROMPT}
      </p>
      <figure className={css.example}>
        <ExamplePicture />
        <figcaption className={css.exampleCaption}>{MANUAL_UPLOAD_EXAMPLE_CAPTION}</figcaption>
      </figure>
      <ul className={css.checklist} aria-label="Your photo needs">
        {MANUAL_UPLOAD_CHECKLIST.map(t => (
          <li key={t} className={css.checkItem}>
            <span className={css.tick} aria-hidden="true">
              ✓
            </span>
            {t}
          </li>
        ))}
      </ul>
      <PhotoPicker
        id="manual-id-file"
        label="Photo 1: your ID"
        hint="The front of your ID, flat, all 4 corners showing."
        file={file}
        preview={preview}
        onPick={f => {
          setFile(f);
          setState('idle');
          setMessage('');
        }}
      />
      <PhotoPicker
        id="manual-id-selfie"
        label="Photo 2: selfie holding your ID"
        hint="You holding the same ID right next to your face."
        file={selfie}
        preview={selfiePreview}
        onPick={f => {
          setSelfie(f);
          setState('idle');
          setMessage('');
        }}
      />
      {!bothChosen ? (
        <p className={css.need} data-manual-id-need="true">
          {!file && !selfie
            ? 'Add both photos to send.'
            : !file
            ? 'Add Photo 1 (your ID) to send.'
            : 'Add Photo 2 (selfie holding your ID) to send.'}
        </p>
      ) : null}
      <button
        type="submit"
        className={css.send}
        disabled={state === 'sending' || !bothChosen}
        aria-disabled={state === 'sending' || !bothChosen}
      >
        {state === 'sending' ? 'Checking…' : 'Send photos for review'}
      </button>
      <p
        ref={statusRef}
        tabIndex={-1}
        className={state === 'check' ? css.checkFail : isProblem ? css.error : css.status}
        role={isProblem ? 'alert' : 'status'}
        aria-live="polite"
        data-manual-id-message={state}
      >
        {message}
      </p>
      <p className={css.note}>{MANUAL_UPLOAD_NOTE}</p>
    </form>
  );
};

export default IdentityManualUpload;
